import React from 'react';
import { Box, Card, CardContent, CardHeader, Divider, Grid, Typography } from '@mui/material';
import { TitleEntity, VersionGroupedByParent } from '@/redux/slices/slice_types';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import TitleTypeLabel from '@/components/_common/TitleTypeLabel';
import TitleVersionChip from '@/components/_common/TitleVersionChip';
import { useAppSelector } from '@/redux/hooks';

interface Props {
  episode: TitleEntity | TitleEntityV2;
  episodeChildren: VersionGroupedByParent;
  parentYear: number;
}

const Episode: React.FC<Props> = ({ episode, episodeChildren, parentYear }) => {
  const versionsByKey = useAppSelector(selectVersionKeys);

  const selectTitleById = (id: string): TitleEntity | TitleEntityV2 | undefined => {
    const titleKey = String(id).substring(1);
    return versionsByKey[titleKey];
  };

  return (
    <Card elevation={0}>
      <CardHeader
        title={
          <Grid container alignItems="center">
            <Grid item>
              <Typography>{episode.name}</Typography>
            </Grid>
            {!!parentYear && (
              <Grid item sx={{ ml: 1, mt: -0.5 }}>
                <Typography variant="caption">({parentYear})</Typography>
              </Grid>
            )}
            <Grid item sx={{ ml: 'auto' }}>
              <TitleTypeLabel type={episode.entity_type} />
            </Grid>
          </Grid>
        }
      />
      <CardContent sx={{ pb: 0, pt: 2 }}>
        <Box>
          {Object.keys(episodeChildren).map((epVersionId) => {
            const epVersion = selectTitleById(epVersionId);
            return (
              <Grid key={epVersionId} container alignItems="center" sx={{ py: 0.5 }}>
                <Grid item maxWidth="50%">
                  <Typography sx={{ wordBreak: 'break-all' }}>E{episode.sequence_order || '?'}</Typography>
                </Grid>
                <Grid item flex="auto">
                  <Divider sx={{ borderColor: 'action.selected', borderStyle: 'dashed', mx: 2 }} />
                </Grid>
                <Grid item>
                  <TitleVersionChip key={epVersionId} version={epVersion} size="small" withTooltip />
                </Grid>
              </Grid>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default Episode;
